import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import type { FornecedorResponse } from '@triade/contracts';
import { useCriarFornecedor, useFornecedores } from '@/api/fornecedores';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

export type FornecedorResumo = Pick<FornecedorResponse, 'id' | 'nome'>;

export interface FornecedorComboboxProps {
  id?: string;
  /** Fornecedor escolhido (null = nenhum). */
  valor: FornecedorResumo | null;
  onSelecionar: (f: FornecedorResumo | null) => void;
  disabled?: boolean;
  /** Pode cadastrar na hora (custoAquisicao.editar). Sem a permissao so escolhe da lista. */
  podeCadastrar: boolean;
  ariaLabel?: string;
}

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/**
 * Fornecedor da vigencia de custo: busca em /fornecedores (so ativos) e "Cadastrar" quando o nome nao existe. A criacao
 * e a da API (POST /fornecedores); a tela so mostra o resultado e seleciona o criado.
 */
export function FornecedorCombobox({ id, valor, onSelecionar, disabled, podeCadastrar, ariaLabel }: FornecedorComboboxProps) {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [erroCriar, setErroCriar] = useState<string | null>(null);
  const listaId = useId();
  const termoAtrasado = useDebounced(termo.trim(), 300);
  const busca = useFornecedores({ ...(termoAtrasado ? { q: termoAtrasado } : {}), ativo: true, limit: 20 }, { ativo: aberto });
  const criar = useCriarFornecedor();
  const nomes = (busca.data ?? []).map((f) => f.nome.toLowerCase());
  const podeOferecerCriar = podeCadastrar && termo.trim().length > 0 && !nomes.includes(termo.trim().toLowerCase());

  async function cadastrar() {
    setErroCriar(null);
    try {
      const criado = await criar.mutateAsync({ nome: termo.trim() });
      onSelecionar({ id: criado.id, nome: criado.nome });
      setAberto(false);
      setTermo('');
    } catch (e) {
      setErroCriar(mensagemApi(e));
    }
  }

  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={listaId}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          disabled={disabled}
          className="flex h-10 w-full items-center gap-2 rounded-[10px] border border-input bg-card px-3 text-left outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={cn('min-w-0 flex-1 truncate', !valor && 'text-ink-disabled')}>{valor ? valor.nome : 'Escolher fornecedor…'}</span>
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-ink-muted" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align="start" sideOffset={6} className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[280px] overflow-hidden rounded-xl border border-border bg-popover shadow-menu">
          <Command shouldFilter={false} label="Buscar fornecedor">
            <CommandInput placeholder="Nome do fornecedor" value={termo} onValueChange={setTermo} />
            <CommandList id={listaId} aria-label="Fornecedores">
              {busca.isError ? (
                <div role="alert" className="px-3 py-4 text-small text-danger-ink">
                  Não foi possível buscar fornecedores: {mensagemApi(busca.error)}
                </div>
              ) : busca.isPending ? (
                <div role="status" className="px-3 py-4 text-small text-ink-muted">
                  Buscando…
                </div>
              ) : (
                <>
                  <CommandEmpty>Nenhum fornecedor ativo com esse nome.</CommandEmpty>
                  {(busca.data ?? []).map((f) => (
                    <CommandItem
                      key={f.id}
                      value={f.id}
                      onSelect={() => {
                        onSelecionar({ id: f.id, nome: f.nome });
                        setAberto(false);
                      }}
                      className={cn(valor?.id === f.id && 'bg-line-soft')}
                    >
                      {f.nome}
                    </CommandItem>
                  ))}
                </>
              )}
              {valor && (
                <CommandItem value="__limpar" onSelect={() => { onSelecionar(null); setAberto(false); }} className="text-ink-secondary">
                  Sem fornecedor
                </CommandItem>
              )}
              {podeOferecerCriar && (
                <CommandItem value="__criar" disabled={criar.isPending} onSelect={() => void cadastrar()} className="font-medium text-primary">
                  {criar.isPending ? 'Cadastrando…' : `Cadastrar “${termo.trim()}”`}
                </CommandItem>
              )}
            </CommandList>
            {erroCriar && (
              <div role="alert" className="border-t border-border px-3 py-2 text-small text-danger-ink">
                {erroCriar}
              </div>
            )}
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
